import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, GripVertical, Maximize2, MoreHorizontal, Pencil, Trash2, type LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Z_INDEX } from "../../lib/z-index";
import { Button, type ButtonProps } from "../../atoms/Button";
import { DropdownMenu } from "../../atoms/DropdownMenu";
import type { DropdownMenuItemProps } from "../../atoms/DropdownMenu/DropdownMenuItem";

// An action's `onClick`/`color` and a menu item's `color` are taken from the props they end up on
// (`Button`, `DropdownMenu.Item`) rather than restated here, so a change to either surfaces as a
// type error instead of silently drifting. An action without a `color` renders `muted`, this
// toolbar's normal tone, rather than Button's own `primary` default.
export interface AnalyticsCardToolbarAction extends Pick<ButtonProps, "onClick" | "color"> {
  id: string;
  icon: LucideIcon;
  label: string;
}

export interface AnalyticsCardToolbarMenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  color?: DropdownMenuItemProps["variant"];
}

/**
 * A floating pill toolbar for AnalyticsCard, composed as `<AnalyticsCard.Toolbar />` among its
 * children. It relies on the card root's `group/card relative`, so it is not exported on its own
 * -- standalone it would neither position nor ever expand.
 *
 * Inset from the card's bottom edge by 8px (bottom-2). This is ONE element, not two stacked ones:
 * at rest it's a small 16px-tall x 40px-wide rounded-full pill; on hover the same element grows in
 * both width and height (to fit however many buttons are configured) while its contents fade/scale
 * in from inside the clipped box. Uses a scoped group (`group/card` + `group-hover/card:`), not the
 * plain/unnamed `group` variant, so nesting inside another hover-reveal component's own `group`
 * doesn't cross-trigger this one.
 *
 * Up to three items, in order: a drag handle (leftmost, only when `dragHandleProps` is given),
 * `actions`, then the ellipsis menu. The handle is purely presentational -- it has no drag logic of
 * its own. `dragHandleProps` is spread onto its underlying button -- dnd-kit's activator props
 * (`listeners` + `attributes` + a callback `ref`) when they come from `SortableList.Item` -- so
 * whichever parent wants reordering owns the actual drag handling; this component only renders the
 * affordance and forwards the props. Omitting them means
 * the card isn't draggable, so no handle renders at all rather than an inert one.
 */
export interface AnalyticsCardToolbarProps {
  className?: string;
  actions?: AnalyticsCardToolbarAction[];
  menuItems?: AnalyticsCardToolbarMenuItem[];
  /**
   * Props spread onto the leading drag-handle button (typically `onPointerDown`, to start a
   * pointer-based reorder drag owned by the caller). Omit them and no handle renders at all.
   * Typed from `Button`'s own props, minus the presentational ones this toolbar fixes itself.
   */
  dragHandleProps?: Omit<ButtonProps, "variant" | "color" | "size" | "iconOnly" | "hoverLabel" | "asChild">;
  /**
   * True while this card's own drag handle is being dragged (owned and reported by the parent
   * board, not tracked here). Forces the toolbar to stay expanded even though the pointer has
   * moved off the card onto other cards/tabs, so the handle being dragged doesn't disappear
   * mid-drag.
   * @default false
   */
  dragging?: boolean;
}

// 28px per icon button (Button's iconOnly + size="sm" compound variant renders `size-7`), 2px gaps
// between buttons, 8px total horizontal padding. The drag handle counts as one more button in this
// math when present -- it's the same size as every other icon button in the toolbar.
const BUTTON_PX = 28;
const GAP_PX = 2;
const PADDING_PX = 8;

export const AnalyticsCardToolbar = ({
  className,
  actions,
  menuItems,
  dragHandleProps,
  dragging = false,
}: AnalyticsCardToolbarProps) => {
  const { t } = useTranslation();

  const resolvedActions: AnalyticsCardToolbarAction[] = actions ?? [
    { id: "expand", icon: Maximize2, label: t("Expand") },
    { id: "edit", icon: Pencil, label: t("Edit") },
  ];
  const resolvedMenuItems: AnalyticsCardToolbarMenuItem[] = menuItems ?? [
    { id: "export", label: t("Export"), icon: Download },
    { id: "delete", label: t("Delete"), icon: Trash2, color: "destructive" },
  ];

  const buttonCount = (dragHandleProps ? 1 : 0) + resolvedActions.length + (resolvedMenuItems.length > 0 ? 1 : 0);
  const expandedWidth = PADDING_PX + buttonCount * BUTTON_PX + Math.max(buttonCount - 1, 0) * GAP_PX;

  const [menuOpen, setMenuOpen] = useState(false);
  const forceOpen = menuOpen || dragging;

  return (
    <div
      data-slot="analytics-card-toolbar"
      style={{ "--toolbar-w": `${expandedWidth}px` }}
      className={cn(
        `absolute bottom-2 left-1/2 ${Z_INDEX.raised} flex h-4 w-10 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full border border-border bg-card shadow-sm transition-all duration-200 ease-out group-hover/card:h-9 group-hover/card:w-[var(--toolbar-w)] group-hover/card:shadow-md group-focus-within/card:h-9 group-focus-within/card:w-[var(--toolbar-w)] group-focus-within/card:shadow-md`,
        forceOpen && "h-9 w-[var(--toolbar-w)] shadow-md",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center gap-0.5 whitespace-nowrap p-1 opacity-0 scale-90 transition-all duration-150 delay-75 ease-out group-hover/card:scale-100 group-hover/card:opacity-100 group-focus-within/card:scale-100 group-focus-within/card:opacity-100",
          forceOpen && "scale-100 opacity-100"
        )}
      >
        {dragHandleProps && (
          <Button
            variant="ghost"
            color="muted"
            size="sm"
            iconOnly
            aria-label={t("Drag to reorder")}
            {...dragHandleProps}
            className={cn("cursor-grab touch-none rounded-full active:cursor-grabbing", dragHandleProps.className)}
          >
            <GripVertical />
          </Button>
        )}
        {resolvedActions.map((action) => {
          const ActionIcon = action.icon;
          return (
            <Button
              key={action.id}
              variant="ghost"
              color={action.color ?? "muted"}
              size="sm"
              iconOnly
              hoverLabel={action.label}
              aria-label={action.label}
              onClick={action.onClick}
              className="rounded-full"
            >
              <ActionIcon />
            </Button>
          );
        })}
        {resolvedMenuItems.length > 0 && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            {/* No hoverLabel here: Button wraps itself in Tooltip (a context provider, not a
                ref-forwarding DOM element) when hoverLabel is set, which breaks
                DropdownMenu.Trigger's asChild click/ref wiring three levels down to the real
                <button> -- the trigger would render but silently never open. aria-label alone
                covers accessibility. */}
            <DropdownMenu.Trigger asChild>
              <Button variant="ghost" color="muted" size="sm" iconOnly aria-label={t("More")} className="rounded-full">
                <MoreHorizontal />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="center" onCloseAutoFocus={(event) => event.preventDefault()}>
              {resolvedMenuItems.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <DropdownMenu.Item key={item.id} onSelect={() => item.onClick?.()} variant={item.color ?? "default"}>
                    {ItemIcon && <ItemIcon />}
                    {item.label}
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
